package diana.soleil.design.iraniancanadienbazzar.feature.events

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.DiscoveryRanking
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.EventCategory
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.stateIn
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

enum class EventFilter { All, ThisWeek, Free, Music, Culture, Family, Business }

/** Recommended = the shared appeal ranking (cover art first, then soonest); Soonest = date only. */
enum class EventSort { Recommended, Soonest }

data class EventsUiState(
    val loading: Boolean = true,
    val events: List<Event> = emptyList(),
    val filter: EventFilter = EventFilter.All,
    val sort: EventSort = EventSort.Recommended,
    /** Event ids the signed-in user marked "I'm going" (empty for guests). */
    val going: Set<String> = emptySet(),
    /** Attendee counts per event id (best-effort one-shot; 0/absent hides the count). */
    val goingCounts: Map<String, Int> = emptyMap(),
    val error: Boolean = false,
) {
    /** Paid placements only (admin-set `featured` flag) — the horizontal Featured rail. */
    val featuredList: List<Event> get() = events.filter { it.featured }
    /** Events the caller RSVP'd to — the horizontal "I'm going" rail. */
    val goingList: List<Event> get() = events.filter { it.id in going }
    // Exclude both featured (Featured rail) and RSVP'd (I'm going rail) so an event never appears
    // in two rails plus Upcoming at once.
    val upcoming: List<Event> get() = events.filterNot { it.featured || it.id in going }
}

@OptIn(ExperimentalCoroutinesApi::class, ExperimentalTime::class)
class EventsViewModel(
    private val repository: EventRepository,
    cityController: CityController,
    private val analytics: AnalyticsTracker,
) : ViewModel() {
    private val retryTrigger = MutableStateFlow(0)
    private val goingCounts = MutableStateFlow<Map<String, Int>>(emptyMap())

    /** Marks/unmarks the caller as going; guests trip [rsvpSignInRequired] and the route bounces. */
    val rsvpSignInRequired = MutableStateFlow(false)

    fun toggleGoing(eventId: String, going: Boolean) {
        viewModelScope.launch {
            when (val result = repository.setRsvp(eventId, going)) {
                is DataResult.Success -> goingCounts.update { counts ->
                    counts + (eventId to ((counts[eventId] ?: 0) + if (going) 1 else -1).coerceAtLeast(0))
                }
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) rsvpSignInRequired.value = true
            }
        }
    }
    private val filter = MutableStateFlow(EventFilter.All)
    private val sort = MutableStateFlow(EventSort.Recommended)

    val state: StateFlow<EventsUiState> =
        combine(
            combine(cityController.activeMetroId, retryTrigger) { metroId, _ -> metroId }
                .flatMapLatest(repository::observeUpcomingEvents)
                // Fetch best-effort attendee counts once per event-list emission.
                .onEach { result ->
                    val ids = (result as? DataResult.Success)?.data?.map { it.id }.orEmpty()
                    if (ids.isNotEmpty()) goingCounts.value = repository.rsvpCounts(ids)
                },
            filter,
            sort,
            repository.observeMyRsvps(),
            goingCounts,
        ) { result, selectedFilter, selectedSort, myRsvps, counts ->
            when (result) {
                is DataResult.Success -> {
                    val now = Clock.System.now().toEpochMilliseconds()
                    EventsUiState(
                        loading = false,
                        events = result.data
                            .filter { (it.endsAtMillis ?: it.startsAtMillis) >= now }
                            .filter { it.matches(selectedFilter, now) }
                            .sortedWith(
                                when (selectedSort) {
                                    // Appeal-first: events with real cover art surface, then soonest.
                                    EventSort.Recommended -> DiscoveryRanking.eventComparator()
                                    EventSort.Soonest -> compareBy { it.startsAtMillis }
                                },
                            ),
                        filter = selectedFilter,
                        sort = selectedSort,
                        going = myRsvps,
                        goingCounts = counts,
                    )
                }
                is DataResult.Failure ->
                    EventsUiState(loading = false, filter = selectedFilter, sort = selectedSort, error = true)
            }
        }.stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5_000),
            initialValue = EventsUiState(),
        )

    fun setFilter(value: EventFilter) {
        filter.value = value
    }

    fun setSort(value: EventSort) {
        sort.value = value
    }

    fun retry() {
        retryTrigger.value += 1
    }
    // trackOutlink moved to EventDetailViewModel (outlinks now fire from the detail page, not the list).
}

private fun Event.matches(filter: EventFilter, now: Long): Boolean = when (filter) {
    EventFilter.All -> true
    EventFilter.ThisWeek -> startsAtMillis in now..(now + SEVEN_DAYS_MILLIS)
    EventFilter.Free -> isFree
    EventFilter.Music -> category == EventCategory.Music
    EventFilter.Culture -> category == EventCategory.Culture
    EventFilter.Family -> category == EventCategory.Family
    EventFilter.Business -> category == EventCategory.Business
}

private const val SEVEN_DAYS_MILLIS = 7L * 24 * 60 * 60 * 1_000
